import { Controller } from "@hotwired/stimulus"

/**
 * Side panel controller using native <dialog>.
 *
 * Opens as a slide-in from the right, closes with an explicit button or ESC.
 * No click-outside-to-close — this is intentional for editing panels where
 * accidental dismissal would lose work.
 *
 * Opening behavior with turbo-frames:
 *   - If the frame already has content (skeleton or previously loaded):
 *     slide in immediately. The skeleton is visible while the real content loads.
 *   - If the frame is empty (lazy, no skeleton):
 *     force-load the frame and wait for the response before sliding in.
 *     This avoids an empty panel sliding in then popping with content 1s later.
 *   - On subsequent opens (content cached): slide in immediately.
 *
 * ESC handling:
 *   The native <dialog> fires a cancelable "cancel" event on ESC.
 *   By default, we animate the close. Children can intercept this by listening
 *   for "cancel" on the dialog and calling e.preventDefault() — e.g. to show
 *   an "unsaved changes" confirmation before allowing the close.
 *
 * Stacking:
 *   Modals can open on top of the side panel (native <dialog> top layer).
 *
 * reset-on-close (value) : le frame reprend son contenu initial (le squelette) à la
 * fermeture — un panel d'éditeur ne montre jamais l'état précédent à la réouverture, et les
 * contrôleurs de l'éditeur fermé se déconnectent (autosave compris).
 *
 * Events dispatched:
 *   - "side-panel:opened" (bubbles) after the panel is shown
 *   - "side-panel:closed" (bubbles) after the panel is closed
 *
 * Window events listened:
 *   - "open-side-panel"  { detail: { id } } — opens the panel matching the id
 *   - "close-side-panel" { detail: { id } } — closes the panel matching the id
 *
 * Global helpers (defined in _side_panel_dialog.html.erb):
 *   openSidePanel(id) / closeSidePanel(id)
 */
export default class extends Controller {
  static targets = ["dialog", "container"]
  static values = { resetOnClose: Boolean }

  connect() {
    this.dialogTarget.addEventListener("cancel", this._onCancel.bind(this))
    this.dialogTarget.addEventListener("close", this._onClose.bind(this))
    if (this.resetOnCloseValue) this._initialFrameContent = this._frame?.innerHTML
  }

  get _frame() {
    return this.element.querySelector("turbo-frame")
  }

  // ==================== Open / Close ====================

  open(event) {
    const eventId = event?.detail?.id
    const elementId = this.element.id

    // Un panneau qui porte un id ne s'ouvre que lorsqu'on le nomme. `selection_receiver` diffuse un
    // événement anonyme pour son panneau legacy, qui n'a pas d'id : sans cette garde il ouvrirait
    // aussi tous les panneaux nommés de la page, dont l'éditeur de composition.
    if (elementId && eventId !== elementId) return

    const turboFrame = this.element.querySelector("turbo-frame")

    if (turboFrame) {
      this._openWithFrame(turboFrame)
    } else {
      this._show()
    }
  }

  close() {
    const dialog = this.dialogTarget
    if (!dialog.open) return

    const beforeClose = new CustomEvent("side-panel:before-close", { bubbles: true, cancelable: true })
    this.element.dispatchEvent(beforeClose)
    if (beforeClose.defaultPrevented) return

    dialog.setAttribute("closing", "")
    dialog.addEventListener("animationend", () => {
      dialog.removeAttribute("closing")
      dialog.close()
    }, { once: true })
  }

  // ==================== Private ====================

  /**
   * Handle opening when the panel contains a turbo-frame.
   *
   * Two cases:
   * 1. Frame has content (skeleton block or previously loaded data):
   *    Open immediately — the user sees the skeleton, then Turbo swaps in
   *    the real content when the response arrives.
   * 2. Frame is empty (no skeleton, lazy src not yet loaded):
   *    Switch to eager loading to force the request (lazy frames inside a
   *    closed <dialog> never intersect, so they'd never load otherwise).
   *    Wait for turbo:frame-load before opening — the slide-in animation
   *    plays with the real content already visible.
   */
  _openWithFrame(frame) {
    if (frame.children.length > 0) {
      this._show()
    } else {
      frame.loading = "eager"
      frame.addEventListener("turbo:frame-load", () => this._show(), { once: true })
      frame.addEventListener("turbo:frame-missing", () => this._show(), { once: true })
    }
  }

  /**
   * ESC handler — animate the close instead of instant dismiss.
   * Children can e.preventDefault() to block closing entirely.
   */
  _onCancel(event) {
    if (!event.defaultPrevented) {
      event.preventDefault()
      this.close()
    }
  }

  _onClose() {
    if (this.resetOnCloseValue && this._frame) this._frame.innerHTML = this._initialFrameContent
    this.element.dispatchEvent(
      new CustomEvent("side-panel:closed", { bubbles: true })
    )
  }

  _show() {
    const dialog = this.dialogTarget
    if (dialog.open) return
    dialog.showModal()
    this.element.dispatchEvent(
      new CustomEvent("side-panel:opened", { bubbles: true })
    )
  }
}
